import { useId, useState } from 'react';
import { Box, Collapse, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ChevronRightIcon } from '@heroicons/react/24/outline';

import { semanticColors, BORDER_RADIUS } from '@/theme/tokens';
import useAnalytics from '@/hooks/useAnalytics';

import { isStepEvent, shouldShowReasoningDisclosure, type NarrationEvent } from './NarrationTypes';
import { AnswerDepth, ResearchDepth, depthTagKey, depthTagKeys } from './AnswerDepth';
import { isAnswerStyleEnabled } from '@/config/env';
import NarrationTimeline from './NarrationTimeline';

/**
 * "How I researched this" — the turn's research trail, kept after the answer.
 *
 * Narration used to be cleared the moment the answer arrived, which meant the
 * reasoning existed only as something that flickered past. For a product whose
 * value is helping a voter understand *why*, the reasoning is worth being able
 * to go back and read: it is where "I used the state voter guide rather than the
 * campaign's own page" is visible.
 *
 * Collapsed by default. Expanded-by-default would push every answer down the
 * page and bury the thing the voter actually asked for, so the default protects
 * the answer and the control makes the reasoning one click away.
 */

interface ReasoningDisclosureProps {
  events: NarrationEvent[];
  /** Horizontal padding, matched to the message body's left edge. */
  px?: number | string;
  /** The depth the turn ran under, and which research agent answered. */
  answerDepth?: AnswerDepth;
  researchDepth?: ResearchDepth;
  /** The answer's language when it differs from the UI's — see `trailLanguageFor`. */
  language?: string;
}

/**
 * Tracked on expand only. Whether voters actually open this is the signal for
 * whether surfacing reasoning earns its place in the UI at all, and a collapse
 * event answers nothing that dwell time on the panel would not. No PII: the
 * reasoning text itself is never sent, only its shape.
 */
const REASONING_EXPANDED_EVENT = 'Chat Reasoning Expanded';

export default function ReasoningDisclosure({
  events,
  px = 2,
  answerDepth,
  researchDepth,
  language,
}: ReasoningDisclosureProps) {
  const { t } = useTranslation('chat');
  const { track } = useAnalytics();
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const tagId = useId();
  const baseTagKey = depthTagKey(answerDepth, researchDepth);
  // Beside Response options' "In-depth research", "Detailed answer" would read
  // as an answer length, so the tag follows the research label.
  const tagKey =
    baseTagKey === depthTagKeys.deep && isAnswerStyleEnabled()
      ? 'depthTag.deepResearch'
      : baseTagKey;

  // Transitions are dropped here so this agrees with `hasResearchTrail`, which
  // discounts them when deciding whether a trail exists. A transition announces
  // routing ("researching in depth") rather than reporting research, so it earns
  // its place in the live stream and not in the trail kept afterwards — without
  // this, a notice could never open the panel but would still appear inside it.
  const trail = events.filter((e) => e.kind !== 'transition');
  if (!trail.length || !shouldShowReasoningDisclosure(trail)) return null;

  const stepCount = trail.filter(isStepEvent).length;

  const handleToggle = () => {
    if (!open) {
      track(REASONING_EXPANDED_EVENT, {
        stepCount,
        toolCategories: [
          ...new Set(
            // `foldNarration` always sets a category on a tool event, but the
            // type permits absence and `undefined` in an array serializes to
            // `null` — a silent hole in the property rather than a dropped one.
            trail.filter(isStepEvent).map((e) => e.toolCategory ?? 'generic')
          ),
        ],
      });
    }
    setOpen((v) => !v);
  };

  return (
    <Box sx={{ mx: px, mb: 1.5, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
        <Box
          component="button"
          type="button"
          onClick={handleToggle}
          aria-expanded={open}
          aria-controls={panelId}
          // The tag describes the control rather than naming it: someone
          // moving control-to-control would otherwise never reach a plain
          // span, and "how this answer was researched" is exactly what they
          // are at this control to find out.
          aria-describedby={tagKey ? tagId : undefined}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.5,
            border: 'none',
            borderRadius: BORDER_RADIUS,
            backgroundColor: 'transparent',
            color: semanticColors.textCaption,
            cursor: 'pointer',
            font: 'inherit',
            fontSize: 12,
            '&:hover': { backgroundColor: semanticColors.surfaceSubtle },
            // Visible keyboard focus — the control is otherwise chromeless.
            '&:focus-visible': {
              outline: `2px solid ${semanticColors.brandPrimary}`,
              outlineOffset: 2,
            },
          }}
        >
          <ChevronRightIcon
            aria-hidden="true"
            style={{
              width: 14,
              height: 14,
              flexShrink: 0,
              transition: 'transform 0.15s ease-out',
              transform: open ? 'rotate(90deg)' : 'none',
            }}
          />
          <Typography component="span" sx={{ fontSize: 12, fontWeight: 500, color: 'inherit' }}>
            {stepCount > 0
              ? t('narration.disclosure.withSteps', { count: stepCount })
              : t('narration.disclosure.label')}
          </Typography>
        </Box>

        {/* Outside the button: it is a record of how the turn was answered,
            not a control, and a reader tabbing through should not have it
            read as part of the toggle's name. */}
        {tagKey && (
          <Typography
            id={tagId}
            component="span"
            sx={{
              px: 0.75,
              py: 0.25,
              borderRadius: BORDER_RADIUS,
              border: `1px solid ${semanticColors.borderLight}`,
              backgroundColor: semanticColors.surfaceSubtle,
              color: semanticColors.textCaption,
              fontSize: 11,
              fontWeight: 500,
              whiteSpace: 'nowrap',
            }}
          >
            {t(tagKey)}
          </Typography>
        )}
      </Box>

      <Collapse in={open} unmountOnExit>
        <Box
          id={panelId}
          sx={{
            mt: 0.5,
            pl: 1,
            borderLeft: `2px solid ${semanticColors.borderLight}`,
          }}
        >
          <NarrationTimeline events={trail} live={false} language={language} />
        </Box>
      </Collapse>
    </Box>
  );
}
